import { YAxisOptions } from 'highcharts/highcharts';
import { WidgetType } from 'types/widget.types';
import { parseFiniteNumberWithNaN } from 'utils/number';

import { DEFAULT_TEXT_STYLE } from '../ChartWidget.config';
import { applyPatterns, replaceGWithBFormatter } from '../ChartWidget.helpers';
import { HighchartsSeriesData } from '../ChartWidget.hooks';
import {
  HIDDEN_AXIS_LINE_COLOR,
  HIDDEN_AXIS_LINE_WIDTH,
  Y_LEGEND_OFFSET,
} from '../DataTableChartCommon/DataTableChartCommon.config';
import { CustomPointSeries, HighchartsOptions } from '../DataTableChartCommon/DataTableChartCommon.types';
import {
  DEFAULT_CHART_DATA,
  DEFAULT_HEIGHT,
  DEFAULT_RADIUS_SIZE,
  DEFAULT_SYMBOL_SIZE,
  DEFAULT_WIDTH,
} from './ScatterPlotChartWidget.config';
import { ScatterPlotChartWidgetData } from './ScatterPlotChartWidget.types';
import { VERSION } from './ScatterPlotChartWidget.upgrade';

export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.ScatterPlotChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      patterns: {
        enabled: false,
        list: [],
      },
      ...DEFAULT_CHART_DATA,
    } as ScatterPlotChartWidgetData,
  };
};

export const getHighChartsScatterPlotConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: ScatterPlotChartWidgetData,
  categories: Array<string>,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;

  options.chart = options.chart || {};
  options.chart.events = { render: CustomLegendRender };
  const { enabled: patternsEnabled, list: patternsList } = widgetData.patterns;

  options.keyCustom = keyCustom;

  options.series = series.map(({ ...otherSeriesData }) => {
    return {
      ...otherSeriesData,
      dataLabels: {
        style: {
          ...DEFAULT_TEXT_STYLE,
          ...widgetData.dataLabels.style,
        },
      },
      marker: {
        symbol: 'circle',
        radius: DEFAULT_RADIUS_SIZE,
        ...widgetData.marker,
      },
      visible: true,
      type: 'scatter',
      xAxis: 0,
      yAxis: 0,
      zIndex: 1,
    };
  });

  options.xAxis = {
    ...(options.xAxis || {}),
    categories,
  };

  const minY2Value = parseFiniteNumberWithNaN(widgetData.seriesMeta?.minY2Value);
  const maxY2Value = parseFiniteNumberWithNaN(widgetData.seriesMeta?.maxY2Value);
  options.yAxis = [
    {
      ...(defaultOptions.yAxis as YAxisOptions),
      labels: {
        ...((defaultOptions.yAxis as YAxisOptions)?.labels || {}),
        formatter: function () {
          return replaceGWithBFormatter.call(this);
        },
      },
    },
    {
      ...defaultOptions.yAxis,
      // We use the x-axis line color and width for all axis settings.
      lineColor: HIDDEN_AXIS_LINE_COLOR,
      lineWidth: HIDDEN_AXIS_LINE_WIDTH,
      max: Number.isNaN(maxY2Value) ? null : maxY2Value,
      min: Number.isNaN(minY2Value) ? null : minY2Value,
      opposite: true,
      title: {
        ...(defaultOptions.yAxis as YAxisOptions)?.title,
        text: widgetData.y2Axis?.title.text ?? '',
      },
      labels: {
        ...((defaultOptions.yAxis as YAxisOptions)?.labels || {}),
        formatter: function () {
          return replaceGWithBFormatter.call(this);
        },
      },
    },
  ];

  if (patternsEnabled) {
    applyPatterns(options, patternsList);
  }

  return options;
};

function CustomLegendRender(this: Highcharts.Chart | undefined) {
  if (this?.legend?.allItems) {
    this.legend.allItems.forEach((item) => {
      const seriesItem = item as CustomPointSeries;
      seriesItem.legendItem?.symbol?.attr({
        y: Y_LEGEND_OFFSET,
        x: Y_LEGEND_OFFSET, // use the same value to align x
        height: DEFAULT_SYMBOL_SIZE, // increase the icon size
        width: DEFAULT_SYMBOL_SIZE,
      });
    });
  }
}
